// นำเข้า dependencies ที่จำเป็น
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";
import usePagination from "../users/pagination";
import "./styles.css";
import { HttpRequest } from "../../component/service/HttpRequest";

// ReportPage Component รับ props จาก App component
function ReportPage({ token, user, setToken, setUser }) {
  // ใช้ navigate hook สำหรับการ redirect
  const navigate = useNavigate();

  // กำหนด state ทั้งหมดที่ใช้ใน component
  const [incidentList, setIncidentList] = useState([]); // เก็บรายการ incident
  const [loading, setLoading] = useState(true); // สถานะการโหลด
  const [error, setError] = useState(null); // เก็บข้อความ error
  const [searchTerm, setSearchTerm] = useState(""); // คำค้นหา
  const [selectedFilter, setSelectedFilter] = useState("Selected.."); // ตัวกรองที่เลือก
  const [filterOptions, setFilterOptions] = useState([]); // ตัวเลือกสำหรับการกรอง
  const [userData, setUserData] = useState(null); // ข้อมูล user

  // เพื่อ state แสดงข้อมูล
  const [isFiltered, setIsFiltered] = useState(false);

  const [filterDataOptions, setFilterDataOptions] = useState({
    incident_date: null, // date picker                 (default blank)
    incident_no: null, // number                      (default blank)
    incident_type: null, // selectbox from getenum      (default null)
    highwayid: null, // textbox                     (default blank)
    highwayname: "", // textbox                     (default blank)
    weathercode: null, // selectbox from getenum      (default null)
    weather: "", // textbox                     (default blank)
    causecode: null, // selectbox from getenum      (default null)
    cause: "", // textbox                     (default blank)
    crash_type: null, // selectbox from getenum      (default null)
    local_direct: null, // selectbox from getenum      (default null)
    aclane_rec: null, // number                      (default blank)
    aclane_lane: null, // selectbox from getenum      (default null)
    locat_plaza: null, // textbox                     (default blank)
    locat_pdet: "", // textbox                     (default blank)
  });

  // เพิ่มฟังก์ชันสำหรับดึงข้อมูล enum
  const [enumData, setEnumData] = useState({
    user_level: [],
    incident_type: [],
    weathercode: [],
    causecode: [],
    crash_type: [],
    local_direct: [],
    aclane_lane: [],
  });

  const fetchEnumData = useCallback(async () => {
    try {
      const data = await HttpRequest("/getenum");

      if (data.result === "OK") {
        setEnumData(data.enum);
        console.log("Loaded enum data:", data.enum);
      }
    } catch (error) {
      console.error("Error fetching enum data:", error);
    }
  }, []);

  // เรียกใช้ตอน component mount
  useEffect(() => {
    fetchEnumData();
  }, [fetchEnumData]);

  // ใช้ custom pagination hook กำหนดให้แสดง 10 รายการต่อหน้า
  const pagination = usePagination(10);

  // useEffect สำหรับโหลดข้อมูล user เมื่อ component mount
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      console.log("Loading stored user data:", storedUser);

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
        console.log("User data loaded successfully:", parsedUser);
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  }, []);

  // useEffect สำหรับเรียกข้อมูลเมื่อ component โหลดหรือมีการเปลี่ยนแปลง pagination
  useEffect(() => {
    console.log("Report Page Mounted or Updated");
    console.log("Current token:", token);
    console.log("Current user data:", userData);

    // console.log("filterDataOptions :", filterDataOptions);

    if (token) {
      fetchIncidentData(token);
    }
  }, [token, pagination.index, pagination.count, filterDataOptions]);

  // ฟังก์ชัน Logout
  const handleLogout = async () => {
    try {
      console.log("Initiating logout process...");

      // แสดง SweetAlert2 เพื่อยืนยันการ logout
      const result = await Swal.fire({
        title: "ยืนยันการออกจากระบบ",
        text: "คุณต้องการออกจากระบบใช่หรือไม่?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "ใช่, ออกจากระบบ",
        cancelButtonText: "ยกเลิก",
      });

      if (result.isConfirmed) {
        console.log("Logout confirmed, clearing data...");

        // เคลียร์ข้อมูลใน localStorage
        localStorage.removeItem("sessiontoken");
        localStorage.removeItem("loginTimestamp");
        localStorage.removeItem("user");

        // เคลียร์ state
        setUserData(null);
        if (typeof setToken === "function") setToken(null);
        if (typeof setUser === "function") setUser(null);

        await Swal.fire({
          title: "ออกจากระบบสำเร็จ",
          text: "คุณได้ออกจากระบบแล้ว",
          icon: "success",
          timer: 1500,
        });

        navigate("/", { replace: true });
      }
    } catch (error) {
      console.error("Logout error:", error);
      await Swal.fire({
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถออกจากระบบได้",
        icon: "error",
      });

      // เคลียร์ข้อมูลและ redirect ในกรณีเกิด error
      localStorage.clear();
      if (typeof setToken === "function") setToken(null);
      if (typeof setUser === "function") setUser(null);
      navigate("/", { replace: true });
    }
  };

  // ฟังก์ชันดึงข้อมูล incident
  const fetchIncidentData = async (authToken) => {
    try {
      setLoading(true);
      console.log("Fetching incident data...");

      let body = {
        index: pagination.index, // ตำแหน่งเริ่มต้นของข้อมูล
        count: pagination.count, // จำนวนรายการที่ต้องการ (10)
        ...filterDataOptions, // ส่งข้อมูลตัวกรอง
      };
      console.log("Request params:", body);


      const data = await HttpRequest("/incident/getincidentlist",body);
      console.log("API Response:", data);

      if (data.result !== "ERROR") {
        const incidents = Array.isArray(data.incidentlist)
          ? data.incidentlist
          : [];
        console.log("Processed incidents:", incidents);

        setIncidentList(incidents);
        pagination.setTotalCount(data.totalincident || 0);

        if (incidents.length > 0) {
          const columns = Object.keys(incidents[0]);
          setFilterOptions(columns);
          setSelectedFilter((prevFilter) => prevFilter || columns[0]);
        }
      } else {
        throw new Error(data.errormessage || "ไม่พบข้อมูล");
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  // ฟังก์ชันจัดการการเปลี่ยนแปลงค่าใน filter
  const handleFilterChange = (field, value) => {
    console.log("Filter Change:", {
      field,
      value,
      currentFilters: filterDataOptions,
    });

    setFilterDataOptions((prev) => {
      const newFilters = {
        ...prev,
        [field]: value === "" ? null : value, // แปลงค่าว่างเป็น null
      };

      // ตรวจสอบว่ามีการใช้ filter หรือไม่
      const hasActiveFilters = Object.values(newFilters).some(
        (val) => val !== null && val !== ""
      );
      setIsFiltered(hasActiveFilters);

      console.log("New Filter Options:", newFilters);
      return newFilters;
    });
  };

  // ฟังก์ชันช่วยหา enum title
  const getEnumTitle = (enumType, id) => {
    const item = enumData[enumType]?.find((item) => item.id === (id ?? null));
    return item?.title || "";
  };


  const renderEnumOptions = (enumType) => {
    return enumData[enumType]?.map((item) => (
      <option
        key={item.id ?? "all"}
        value={item.id ?? ""} // ค่า id จะถูกส่งไปยัง handleFilterChange
      >
        {item.title} {/*แสดงค่า title จาก API โดยตรง */}
      </option>
    ));
  };

  // ฟังก์ชั่นวันที่ 
 const formatDate = (dateString) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  const englishMonths = [
    "January",
    "February", 
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];

 // return `${day} ${englishMonths[month]} ${year} ${hours}:${minutes}`;
  return `${day} ${englishMonths[month]} ${year} `;
};

  // ส่วนแสดงผล UI
  return (
    <div className="report-container">
      {/* Navbar */}
      <div className="blank"></div>
      <nav className="navbar">
        <div className="nav-brand">
          <h2>INCIDENT REPORT</h2>
        </div>
        <div className="nav-links">
          <span className="user-welcome">
            USER: {userData?.username || "Unknown"}
          </span>
          <button onClick={handleLogout} className="logout-btn">
            LOGOUT
          </button>
        </div>
      </nav>

      {/* Content */}
      <div className="report-content">
        {loading ? (
          <div className="loading">กำลังโหลดข้อมูล...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            <div className="filters-section">
              <div className="search-controls">
                <div className="filters-subtitle">
                  {formatDate(new Date().toISOString())}
                </div>
              </div>
            </div>
            {/* Table Section */}
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      {/** AC Lane Lane */}
                      <div className="style-header-column">
                        <span>AC Lane Lane</span>
                        <select
                          onChange={(e) =>
                            handleFilterChange("aclane_lane", e.target.value)
                          }
                          value={filterDataOptions.aclane_lane ?? ""}
                        >
                          {renderEnumOptions("aclane_lane")}
                        </select>
                      </div>
                    </th>

                    <th>
                      {/** AC Lane Rec */}
                      <div className="style-header-column">
                        <span>AC Lane Rec</span>
                        <input
                          type="number"
                          onChange={(e) =>
                            handleFilterChange("aclane_rec", e.target.value)
                          }
                          value={filterDataOptions.aclane_rec || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** AD Reptime */}
                      <div className="style-header-column">
                        <span>AD Reptime</span>
                        <input
                          type="number"
                          onChange={(e) =>
                            handleFilterChange("ad_reptime", e.target.value)
                          }
                          value={filterDataOptions.ad_reptime || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/**Cause */}
                      <div className="style-header-column">
                        <span>Cause</span>
                        <input
                          type="text"
                          onChange={(e) =>
                            handleFilterChange("cause", e.target.value)
                          }
                          value={filterDataOptions.cause || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/**Cause Code */}
                      <div className="style-header-column">
                        <span>Cause Code</span>
                        <select
                          onChange={(e) =>
                            handleFilterChange("causecode", e.target.value)
                          }
                          value={filterDataOptions.causecode ?? ""}
                        >
                          {/* <option value="">-- เลือกทั้งหมด --</option> */}
                          {renderEnumOptions("causecode")}
                        </select>
                      </div>
                    </th>

                    <th>
                      {/** Crash Type */}
                      <div className="style-header-column">
                        <span>Crash Type</span>
                        <select
                          onChange={(e) =>
                            handleFilterChange("crash_type", e.target.value)
                          }
                          value={filterDataOptions.crash_type ?? ""}
                        >
                          {/* <option value="">-- เลือกทั้งหมด --</option> */}
                          {renderEnumOptions("crash_type")}
                        </select>
                      </div>
                    </th>

                    <th>
                      {/** Deadfemale Amount */}
                      <div className="style-header-column">
                        <span>Deadfemale Amount</span>
                        <input
                          type="number"
                          onChange={(e) =>
                            handleFilterChange(
                              "deadfemaleamount",
                              e.target.value
                            )
                          }
                          value={filterDataOptions.deadfemaleamount || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Deadmale Amount */}
                      <div className="style-header-column">
                        <span>Deadmale Amount</span>
                        <input
                          type="number"
                          onChange={(e) =>
                            handleFilterChange("deadmaleamount", e.target.value)
                          }
                          value={filterDataOptions.deadmaleamount || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Highway ID */}
                      <div className="style-header-column">
                        <span>Highway ID</span>
                        <input
                          type="text"
                          onChange={(e) =>
                            handleFilterChange("highwayid", e.target.value)
                          }
                          value={filterDataOptions.highwayid || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Highway Name */}
                      <div className="style-header-column">
                        <span>Highway Name</span>
                        <input
                          type="text"
                          onChange={(e) =>
                            handleFilterChange("highwayname", e.target.value)
                          }
                          value={filterDataOptions.highwayname || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Incident Date */}
                      <div className="style-header-column">
                        <span>Incident Date</span>
                        <input
                          type="datetime-local"
                          onChange={(e) =>
                            handleFilterChange("incident_date", e.target.value)
                          }
                          value={filterDataOptions.incident_date || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Incident No */}
                      <div className="style-header-column">
                        <span>Incident No</span>
                        <input
                          type="number"
                          onChange={(e) =>
                            handleFilterChange("incident_no", e.target.value)
                          }
                          value={filterDataOptions.incident_no || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Incident Type */}
                      <div className="style-header-column">
                        <span>Incident Type</span>
                        <select
                          onChange={(e) =>
                            handleFilterChange("incident_type", e.target.value)
                          }
                          value={filterDataOptions.incident_type ?? ""}
                        >
                          {/* <option value="">-- เลือกทั้งหมด --</option> */}
                          {renderEnumOptions("incident_type")} // จะแสดง: all,
                          accident, car_crash, other
                        </select>
                      </div>
                    </th>

                    <th>
                      {/** Latitude */}
                      <div className="style-header-column">
                        <span>Latitude</span>
                        <input
                          type="number"
                          onChange={(e) =>
                            handleFilterChange("latitude", e.target.value)
                          }
                          value={filterDataOptions.latitude || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Local Direct */}
                      <div className="style-header-column">
                        <span>Local Direct</span>
                        <select
                          onChange={(e) =>
                            handleFilterChange("local_direct", e.target.value)
                          }
                          value={filterDataOptions.local_direct ?? ""}
                        >
                          {/* <option value="">-- เลือกทั้งหมด --</option> */}
                          {renderEnumOptions("local_direct")}
                        </select>
                      </div>
                    </th>

                    <th>
                      {/** Locat PDet */}
                      <div className="style-header-column">
                        <span>Locat PDet</span>
                        <input
                          type="text"
                          onChange={(e) =>
                            handleFilterChange("locat_pdet", e.target.value)
                          }
                          value={filterDataOptions.locat_pdet || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Locat Plaza */}
                      <div className="style-header-column">
                        <span>Locat Plaza</span>
                        <input
                          type="text"
                          onChange={(e) =>
                            handleFilterChange("locat_plaza", e.target.value)
                          }
                          value={filterDataOptions.locat_plaza || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/**Locat Range */}
                      <div className="style-header-column">
                        <span>Locat Range</span>
                        <input
                          type="number"
                          onChange={(e) =>
                            handleFilterChange("locat_range", e.target.value)
                          }
                          value={filterDataOptions.locat_range || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/**Longitude */}
                      <div className="style-header-column">
                        <span>Longitude</span>
                        <input
                          type="number"
                          onChange={(e) =>
                            handleFilterChange("longitude", e.target.value)
                          }
                          value={filterDataOptions.longitude || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Transaction Datetime */}
                      <div className="style-header-column">
                        <span>Transaction Datetime</span>
                        <input
                          type="datetime-local"
                          onChange={(e) =>
                            handleFilterChange(
                              "transactiondatetime",
                              e.target.value
                            )
                          }
                          value={filterDataOptions.transactiondatetime || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Weather */}
                      <div className="style-header-column">
                        <span>Weather</span>
                        <input
                          type="text"
                          onChange={(e) =>
                            handleFilterChange("weather", e.target.value)
                          }
                          value={filterDataOptions.weather || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/** Weather Code */}
                      <div className="style-header-column">
                        <span>Weather Code</span>
                        <select
                          onChange={(e) =>
                            handleFilterChange("weathercode", e.target.value)
                          }
                          value={filterDataOptions.weathercode ?? ""}
                        >
                          {/* <option value="">-- เลือกทั้งหมด --</option> */}
                          {renderEnumOptions("weathercode")} // จะแสดง: all,
                          accident, car_crash, other
                        </select>
                      </div>
                    </th>

                    <th>
                      {/**Wounded Female Amount */}
                      <div className="style-header-column">
                        <span>Wounded Female Amount</span>
                        <input
                          type="number"
                          onChange={(e) =>
                            handleFilterChange(
                              "woundedfemaleamount",
                              e.target.value
                            )
                          }
                          value={filterDataOptions.woundedfemaleamount || ""}
                        />
                      </div>
                    </th>

                    <th>
                      {/**Wounded Male Amount */}
                      <div className="style-header-column">
                        <span>Wounded Male Amount</span>
                        <input
                          type="number"
                          onChange={(e) =>
                            handleFilterChange(
                              "woundedmaleamount",
                              e.target.value
                            )
                          }
                          value={filterDataOptions.woundedmaleamount || ""}
                        />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {incidentList.length > 0 ? (
                    incidentList.map((item, index) => (
                      <tr key={index}>
                        {/* <td>{item.index || ""}</td> */}
                        <td>{getEnumTitle("aclane_lane", item.aclane_lane)}</td>
                        <td>{item.aclane_rec || ""}</td>
                        <td>{item.ad_reptime || ""}</td>
                        <td>{item.cause || ""}</td>
                        <td>{getEnumTitle("causecode", item.causecode)}</td>
                        <td>{getEnumTitle("crash_type", item.crash_type)}</td>
                        <td>{item.deadfemaleamount || ""}</td>
                        <td>{item.deadmaleamount || ""}</td>
                        <td>{item.highwayid || ""}</td>
                        <td>{item.highwayname || ""}</td>
                        <td>{formatDate(item.incident_date) || ""}</td>
                        <td>{item.incident_no || ""}</td>
                        <td>
                          {getEnumTitle("incident_type" || item.incident_type)}
                        </td>
                        <td>{item.latitude || ""}</td>
                        <td>
                          {getEnumTitle("local_direct", item.local_direct)}
                        </td>
                        <td>{item.locat_pdet || ""}</td>
                        <td>{item.locat_plaza || ""}</td>
                        <td>{item.locat_range || ""}</td>
                        <td>{item.longitude || ""}</td>
                        <td>{formatDate(item.transactiondatetime)|| ""}</td>
                        <td>{item.weather || ""}</td>
                        <td>{item.weathercode || ""}</td>
                        <td>{item.woundedfemaleamount || ""}</td>
                        <td>{item.woundedmaleamount || ""}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="16">
                        <div className="no-data">
                          <div className="cross-icon-container">
                            <FaTimes className="cross-icon" />
                          </div>
                          <p>ไม่พบข้อมูล</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {incidentList.length > 0 && (
              <div className="pagination-container">
                <button
                  onClick={pagination.firstPage}
                  disabled={pagination.currentPage === 1}
                  className="pagination-btn"
                >
                  {" "}
                  {/** Frist Page */}
                  {"<<"}
                </button>
                <button
                  onClick={pagination.prevPage}
                  disabled={pagination.currentPage === 1}
                  className="pagination-btn"
                >
                  {" "}
                  {/** Previous Page */}
                  {"<"}
                </button>
                <span className="pagination-info">
                  {pagination.currentPage}/{pagination.totalPages || 1}
                </span>
                <button
                  onClick={pagination.nextPage}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="pagination-btn"
                >
                  {" "}
                  {/** Next Page */}
                  {">"}
                </button>
                <button
                  onClick={pagination.lastPage}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="pagination-btn"
                >
                  {" "}
                  {/** Last Page */}
                  {">>"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ReportPage;
